import React, { useEffect, useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { TailSpin } from "react-loader-spinner";
import { getApi } from "../../../utils/api";
import { MENU_USER } from "../../../config/Endpoints";
import { toast } from "react-toastify";
import { logoutUser } from "../../../utils/Storage";
import {
  dashboardRouteComponents,
  dbIndependentRoutes,
} from "../../../config/routeComponents";
import { DashboardLayout } from "./dashboard-layout.tsx";
import RouteWrapper from "./route-wrapper.tsx";
import { getPerformanceLensWorkspaceDestination } from "../../lib/performance-lens/workspace-navigation.ts";
import {
  isRestrictedForSQELabsMenu,
  isRestrictedForSQELabsPath,
} from "../../utils/sqe-labs-navigation-access.ts";
import { useSqeLabs } from "../../hooks/use-sqe-labs.ts";

const initiativePathAliases: Record<string, string> = {
  "/growthActivities": "/elevateYourself",
  "/growthInsights": "/reviewInitiatives",
};

const routeComponents = dashboardRouteComponents as Record<
  string,
  React.ComponentType<any> | undefined
>;

function updateInitiativesMenuPath(item: any): any {
  return {
    ...item,
    achorValue: initiativePathAliases[item.achorValue] || item.achorValue,
    subMenu: item.subMenu?.filter((subItem: any) => subItem.achorValue !== "/teamGrowth").map(updateInitiativesMenuPath),
  };
}

const performanceLensDescriptions: Record<string, string> = {
  "/giveFeedback": "Review your teammates each feedback cycle.",
  "/selfCheck": "Reflect on your own work each feedback cycle.",
  "/myFeedback": "View your published, confidential feedback.",
  "/adminDashboard": "Review Performance Lens results and submissions.",
  "/performanceConfiguration": "Manage Performance Lens cycles and questions.",
  "/hodDashboard": "See feedback progress and published aggregates for your delivery projects.",
  "/myPerformance": "Give feedback, complete your self check, and view published results.",
  "/performanceDashboard": "Manage performance cycles and track progress.",
};

function Home() {
  const isSQELabsUser = useSqeLabs();
  const [menuList, setMenuList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean | null>(null);

  const accessToken = Cookies.get("accessToken");
  const navigate = useNavigate();
  const [pageMeta, setPageMeta] = useState({
    title: "",
    description: "",
    isHome: false,
  });

  useEffect(() => {
    setIsLoading(true);

    getApi(MENU_USER, accessToken)
      .then((res) => {
        if (res.data.code === "200") {
          setMenuList(res.data.data
            .filter((item: any) => item.achorValue !== "/teamGrowth")
            .map(updateInitiativesMenuPath)
            .filter((item: any) => !Array.isArray(item.subMenu) || item.subMenu.length > 0 || item.achorValue));
        } else if (res.data.code === "500") {
          toast.error("Something went wrong please try again !!");
        } else {
          navigate("/");
        }
      })
      .catch((e) => {
        toast.error("Something went wrong please try again !!");
        logoutUser();
        console.log(e);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  return (
    <>
      {isLoading && (
        <div className="overlay">
          <div className="loader-container">
            <TailSpin
              height="100"
              width="100"
              radius="1"
              color="blue"
              ariaLabel="tail-spin-loading"
              visible={true}
            />
          </div>
        </div>
      )}

      <div className="wrapper">
        {!isLoading && isLoading !== null && (
          <>
            <Routes>
              <Route
                path="/dashboard"
                element={
                  <DashboardLayout menu={menuList} pageMeta={pageMeta} />
                }
              >
                {/* 🔹 Dynamic Routes from API */}
                {menuList?.map((item, index) => {
                  const Component = routeComponents[item.achorValue];
                  const workspaceDestination = getPerformanceLensWorkspaceDestination(item.achorValue);
                  const restrictedForSQELabs =
                    isSQELabsUser &&
                    (isRestrictedForSQELabsMenu(item.name) ||
                      isRestrictedForSQELabsPath(item.achorValue));

                  if (item.achorValue && restrictedForSQELabs) {
                    return (
                      <Route
                        key={`restricted-main-${index}`}
                        path={item.achorValue.slice(1)}
                        element={<Navigate to="/dashboard" replace />}
                      />
                    );
                  }

                  // 🏠 Home (/dashboard)
                  if (item.achorValue === "/" && Component) {
                    return (
                      <Route
                        key={`main-${index}`}
                        index
                        element={
                          <RouteWrapper
                            meta={{
                              title: item.name,
                              description: item.description,
                              isHome: true,
                            }}
                            setPageMeta={setPageMeta}
                          >
                            <Component
                              pageMeta={{
                                title: item.name,
                                description: item.description,
                              }}
                            />
                          </RouteWrapper>
                        }
                      />
                    );
                  }

                  return (
                    <React.Fragment key={`frag-${index}`}>
                      {/* 🔹 Main Menu Route */}
                      {item.achorValue && (Component || workspaceDestination) && (
                        <Route
                          path={item.achorValue.slice(1)}
                          element={
                            workspaceDestination ? <Navigate to={`/dashboard${workspaceDestination}`} replace /> : <RouteWrapper
                              meta={{
                                title: item.name,
                                description: item.description,
                                isHome: false,
                              }}
                              setPageMeta={setPageMeta}
                            >
                              <Component
                                pageMeta={{
                                  title: item.name,
                                  description: item.description,
                                }}
                              />
                            </RouteWrapper>
                          }
                        />
                      )}

                      {/* 🔹 Sub Menu Routes */}
                      {item.subMenu?.map((subItem: any, i: number) => {
                        const SubComponent = routeComponents[subItem.achorValue];
                        const subWorkspaceDestination = getPerformanceLensWorkspaceDestination(subItem.achorValue);
                        const restrictedSubRoute =
                          isSQELabsUser &&
                          (isRestrictedForSQELabsMenu(subItem.name) ||
                            isRestrictedForSQELabsPath(subItem.achorValue));

                        return (
                          restrictedSubRoute ? (
                            <Route
                              key={`restricted-sub-${i}`}
                              path={subItem.achorValue.slice(1)}
                              element={<Navigate to="/dashboard" replace />}
                            />
                          ) : (SubComponent || subWorkspaceDestination) && (
                            <Route
                              key={`sub-${i}`}
                              path={subItem.achorValue.slice(1)}
                              element={
                                subWorkspaceDestination ? <Navigate to={`/dashboard${subWorkspaceDestination}`} replace /> : <RouteWrapper
                                  meta={{
                                    title: subItem.name,
                                    description: performanceLensDescriptions[subItem.achorValue] ?? subItem.description,
                                    isHome: false,
                                  }}
                                  setPageMeta={setPageMeta}
                                >
                                  <SubComponent
                                    pageMeta={{
                                      title: subItem.name,
                                      description: performanceLensDescriptions[subItem.achorValue] ?? subItem.description,
                                    }}
                                  />
                                </RouteWrapper>
                              }
                            />
                          )
                        );
                      })}
                    </React.Fragment>
                  );
                })}

                {Object.entries(initiativePathAliases)
                  .filter(([, newPath]) => menuList.some(
                    (item) => item.achorValue === newPath || item.subMenu?.some((subItem: any) => subItem.achorValue === newPath),
                  ))
                  .map(([oldPath, newPath]) => (
                    <Route key={oldPath} path={oldPath.slice(1)} element={<Navigate to={`/dashboard${newPath}`} replace />} />
                  ))}
                <Route path="teamGrowth" element={<Navigate to="/dashboard" replace />} />

                {/* 🔹 Independent Routes */}
                {dbIndependentRoutes.map((item, index) => {
                  const Component = routeComponents[item.path];
                  if (
                    isSQELabsUser &&
                    (isRestrictedForSQELabsMenu(item.title) ||
                      isRestrictedForSQELabsPath(item.path))
                  ) {
                    return (
                      <Route
                        key={`restricted-ind-${index}`}
                        path={item.path.slice(1)}
                        element={<Navigate to="/dashboard" replace />}
                      />
                    );
                  }

                  if (!Component) return null;

                  return (
                    <Route
                      key={`ind-${index}`}
                      path={item.path.slice(1)}
                      element={
                        <RouteWrapper
                          meta={{
                            title: item.title,
                            description: item.description,
                            isHome: false,
                          }}
                          setPageMeta={setPageMeta}
                        >
                          <Component
                            pageMeta={{
                              title: item.title,
                              description: item.description,
                            }}
                          />
                        </RouteWrapper>
                      }
                    />
                  );
                })}
              </Route>
            </Routes>{" "}
          </>
        )}
      </div>
    </>
  );
}

export default Home;
